import React from "react"
import { useDispatch } from "react-redux"
import { motion } from "framer-motion"
import { styled, Theme } from "@mui/material/styles"
import Stack from "@mui/material/Stack"
import Typography from "@mui/material/Typography"
import Box from "@mui/material/Box"
import { NAVIGATE_TO_SETTINGS_PAGE, PROFILE_IMAGE_CLICKED, SHOW_SHARE_PROFILE_MODAL } from "constants/events"
import { MINIMIZED_PROFILE_BANNER_HEIGHT, OWNER, PROFILE, SETTINGS, VIEWER } from "constants/index"
import { useAppSelector } from "appRedux/hooks"
import { updateShowEnlargedAvatarModal } from "appRedux/slices/globalSlice"
import useEventListener from "hooks/useEventListener"
import SettingsIcon from "components/atoms/_icons/SettingsIcon"
import ShareIcon from "components/atoms/_icons/ShareIcon"
import IconWrapper from "components/atoms/IconWrapper"
import ProfileImage from "components/molecules/ProfileImage"
import BioSection from "components/molecules/ProfileBanner/BioSection"
import LoadingPlaceholder from "components/molecules/ProfileBanner/LoadingPlaceholder"
import withRoleBasedPermission from "components/HOCs/withRoleBasedPermission"

const AVATAR_ENLARGE_TRANSITION_DURATION = 0.3

type ProfileBannerProps = {
  userProfileData: Profile | null;
  loading: boolean;
  isMinimized: boolean;
  isFixed: boolean;
}

const IconWrapperWithRoleBasedPermission = withRoleBasedPermission(IconWrapper)

export default function ProfileBanner(props: ProfileBannerProps) {
  const { userProfileData, loading, isMinimized, isFixed } = props

  const role = useAppSelector((state) => state.global.role)
  const showEnlargedAvatar = useAppSelector((state) => state.global.showEnlargedAvatar)

  const dispatch = useDispatch()

  useEventListener(document, PROFILE_IMAGE_CLICKED, toggleEnlargedAvatar)

  function toggleEnlargedAvatar() {
    if (!userProfileData?.avatar || isFixed) return
    dispatch(updateShowEnlargedAvatarModal(!showEnlargedAvatar))
  }

  function handleSettingsButtonClicked() {
    document.dispatchEvent(new CustomEvent(NAVIGATE_TO_SETTINGS_PAGE))
  }

  function handleShareIconClicked() {
    document.dispatchEvent(new CustomEvent(SHOW_SHARE_PROFILE_MODAL))
  }

  return (
    <Box width={"100%"}>
      <Container
        initial={{ height: "166px" }}
        animate={{ height: isMinimized ? `${MINIMIZED_PROFILE_BANNER_HEIGHT}px` : "166px" }}
        transition={{ duration: 0.2 }}
        isfixed={isFixed ? 1 : 0}
        style={{
          marginTop: role === OWNER ? 0 : (isFixed ? 0 : "-96px")
        }}
      >
        {isFixed ? null : (role === VIEWER ? <ShareIconWrapper onClick={handleShareIconClicked}>
          <ShareIcon/>
        </ShareIconWrapper> : <IconWrapperWithRoleBasedPermission
          resource={PROFILE}
          action={SETTINGS}
          onClick={() => {
          }}
        >
          <></>
        </IconWrapperWithRoleBasedPermission>)}
        {!loading && userProfileData ? <Wrapper isfixed={isFixed ? 1 : 0}>
          <ProfileImageWrapper
            initial={{ width: "100px", height: "100px" }}
            animate={{ width: isMinimized ? "50px" : "100px", height: isMinimized ? "50px" : "100px" }}
            transition={{ duration: 0.2 }}
          >
            <ProfileImage
              imageSrc={userProfileData.avatar}
              isFixed={isFixed}
              transitionDuration={AVATAR_ENLARGE_TRANSITION_DURATION}
            />
            {isMinimized && <MinimizedUsername>{userProfileData?.handle}@</MinimizedUsername>}
          </ProfileImageWrapper>
          <BioSection
            show={!isMinimized}
            handle={userProfileData?.handle}
            name={userProfileData?.name}
            birthday={userProfileData?.birthday}
            avatarTransitionDuration={AVATAR_ENLARGE_TRANSITION_DURATION}
          />
        </Wrapper> : <LoadingPlaceholder/>}
        <IconWrapperWithRoleBasedPermission
          resource={PROFILE}
          action={SETTINGS}
          onClick={handleSettingsButtonClicked}
        >
          <SettingsIcon/>
        </IconWrapperWithRoleBasedPermission>
        {isFixed ? null : (role === VIEWER ? <IconWrapper>
          <></>
        </IconWrapper> : null)}
      </Container>
    </Box>
  )
}

type ContainerProps = {
  theme?: Theme;
  isfixed: 1 | 0;
}

export const Container = styled(motion.div)(({ theme, isfixed }: ContainerProps) => ({
  width: "100%",
  maxWidth: "var(--max-container-width)",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  position: isfixed ? "fixed" : "relative",
  top: isfixed ? "0" : "initial",
  zIndex: isfixed ? 2 : "unset",
  backgroundColor: theme?.palette.gray[2],

  "& svg": {
    stroke: theme?.palette.gray["13"]
  }
}))

const Wrapper = styled(Stack)(({ isfixed }: ContainerProps) => ({
  width: "100%",
  flexDirection: isfixed ? "row" : "column",
  alignItems: "center",
  justifyContent: "space-between",
  marginLeft: isfixed ? "12px" : 0
}))

export const ProfileImageWrapper = styled(motion.div)(() => ({
  position: "relative",
  display: "flex",
  alignItems: "center",
  marginTop: "4px"
}))

export const MinimizedUsername = styled(Typography)(({ theme }) => ({
  width: "fit-content",
  height: "24px",
  fontSize: "1.4rem",
  fontWeight: 500,
  color: theme.palette.gray["16"],
  textAlign: "center",
  borderRadius: "8px",
  padding: "2px 12px"
}))

const ShareIconWrapper = styled(Stack)(({ theme }) => ({
  width: "44px",
  height: "44px",
  flexShrink: 0,
  justifyContent: "center",
  alignItems: "center",
  cursor: "pointer",
  zIndex: 5,

  "& svg": {
    stroke: "none",

    "& path": {
      fill: theme.palette.gray[13]
    }
  }
}))
